// PR-28: offline capture queue.
//
// User taps Send while offline (or the request fails with a network error)
// → enqueue() persists the fact in IndexedDB. flush() drains the queue,
// calling the supplied send function on each entry. Successful entries
// are removed; failures are retried with exponential backoff (1s, 2s, 4s,
// 8s) up to MAX_ATTEMPTS, after which they're left in the queue (visible
// to the user) but stop being auto-retried.
//
// Queue ids are local UUIDs (not server card_ids) so the entry stays
// addressable even if multiple flushes overlap. The server doesn't see
// these ids — once the agent's write_card lands, the SSE refresh shows
// the real card and we drop the queue entry.

import { deleteValue, getAll, putValue } from './indexed_db'

const STORE = 'capture_queue'
const MAX_ATTEMPTS = 5

export interface QueuedCapture {
  id: string
  project_id: string
  text: string
  created_at: number
  attempts: number
  last_error?: string
  /** Earliest time we should retry this entry. Computed on each failure
   *  via exponential backoff. Bypassed on a fresh `online` event flush. */
  next_retry_at?: number
}

function uuid(): string {
  // crypto.randomUUID is supported in modern browsers (Chrome 92+, Safari 15.4+).
  // Fallback to a Math.random-based id if missing — the tradeoff is
  // collision risk that's astronomically low for our scale (<10s of
  // queued items per user).
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return (
    'q-' +
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2, 10)
  )
}

export async function enqueue(
  project_id: string,
  text: string,
): Promise<QueuedCapture> {
  const entry: QueuedCapture = {
    id: uuid(),
    project_id,
    text,
    created_at: Date.now(),
    attempts: 0,
  }
  await putValue(STORE, entry)
  return entry
}

export async function listAll(): Promise<QueuedCapture[]> {
  const all = await getAll<QueuedCapture>(STORE)
  // Sort by created_at so flushes happen in capture order — preserves the
  // user's intent (the first idea they captured arrives at the agent first).
  return all.sort((a, b) => a.created_at - b.created_at)
}

export async function listForProject(project_id: string): Promise<QueuedCapture[]> {
  const all = await listAll()
  return all.filter((q) => q.project_id === project_id)
}

export async function remove(id: string): Promise<void> {
  await deleteValue(STORE, id)
}

async function recordFailure(entry: QueuedCapture, error: string): Promise<void> {
  const attempts = entry.attempts + 1
  // 1s, 2s, 4s, 8s; final attempt has no next-retry (caller stops).
  const backoffMs = attempts >= MAX_ATTEMPTS ? 0 : 1000 * 2 ** (attempts - 1)
  const updated: QueuedCapture = {
    ...entry,
    attempts,
    last_error: error,
    next_retry_at: backoffMs > 0 ? Date.now() + backoffMs : undefined,
  }
  await putValue(STORE, updated)
}

export interface FlushResult {
  sent: number
  failed: number
  /** Items past MAX_ATTEMPTS — surfaced to the caller for UI. */
  giving_up: QueuedCapture[]
}

/**
 * Drain the queue: call `send` on each entry. Success → remove. Network
 * failure → record + leave in place. Items past MAX_ATTEMPTS stay in the
 * queue but stop being auto-retried.
 *
 * Caller controls when to call: typically on `online` event AND optimistically
 * on each successful API call from anywhere in the app.
 *
 * Concurrency: a module-scoped guard prevents overlapping flushes. If a
 * flush is in progress, subsequent calls return immediately with zeros.
 * This avoids the "online event fires + a previous flush is still in
 * flight" double-send scenario.
 */
let flushing = false

export async function flush(
  send: (q: QueuedCapture) => Promise<void>,
  opts?: { ignoreBackoff?: boolean; project_id?: string },
): Promise<FlushResult> {
  if (flushing) return { sent: 0, failed: 0, giving_up: [] }
  flushing = true
  const result: FlushResult = { sent: 0, failed: 0, giving_up: [] }
  try {
    const all = await listAll()
    const items = opts?.project_id
      ? all.filter((q) => q.project_id === opts.project_id)
      : all
    const now = Date.now()
    for (const item of items) {
      // Skip items that have given up entirely.
      if (item.attempts >= MAX_ATTEMPTS) {
        result.giving_up.push(item)
        continue
      }
      // Respect backoff unless explicitly ignored (online events bypass).
      if (
        !opts?.ignoreBackoff &&
        item.next_retry_at !== undefined &&
        item.next_retry_at > now
      ) {
        continue
      }
      try {
        await send(item)
        await remove(item.id)
        result.sent++
      } catch (e) {
        const msg = (e as Error)?.message ?? String(e)
        await recordFailure(item, msg)
        result.failed++
      }
    }
  } finally {
    flushing = false
  }
  return result
}

export const _MAX_ATTEMPTS = MAX_ATTEMPTS
